import { Drawer, Group, ScrollArea, Stack, Text, TextInput, UnstyledButton } from "@mantine/core";
import { IconCheck, IconPointFilled, IconSearch, IconX } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import type { DropDownOption } from "@/types/common.types";
import styles from "./add-user.module.scss";

export type EntityRuleDrawerKind = "inclusion" | "exclusion";

export interface EntityRuleDrawerProps {
  opened: boolean;
  onClose: () => void;
  kind: EntityRuleDrawerKind;
  ruleTypeLabel: string;
  options: DropDownOption[];
  selectedValueIds: string[];
}

export function EntityRuleDrawer({
  opened,
  onClose,
  kind,
  ruleTypeLabel,
  options,
  selectedValueIds,
}: EntityRuleDrawerProps) {
  const [searchValue, setSearchValue] = useState("");
  const [localSelectedIds, setLocalSelectedIds] = useState<string[]>(selectedValueIds);

  useEffect(() => {
    if (opened) {
      setLocalSelectedIds(selectedValueIds);
      setSearchValue("");
    }
  }, [opened, selectedValueIds]);

  const includedOptions = useMemo(() => {
    // For inclusions, "Included" = explicitly selected values.
    // For exclusions, "Included" = everything NOT explicitly excluded.
    if (kind === "inclusion") {
      return options.filter((opt) => localSelectedIds.includes(opt.value));
    }
    return options.filter((opt) => !localSelectedIds.includes(opt.value));
  }, [options, localSelectedIds, kind]);

  const excludedOptions = useMemo(() => {
    // For inclusions, "Excluded" = everything NOT explicitly included.
    // For exclusions, "Excluded" = explicitly selected values.
    if (kind === "inclusion") {
      return options.filter((opt) => !localSelectedIds.includes(opt.value));
    }
    return options.filter((opt) => localSelectedIds.includes(opt.value));
  }, [options, localSelectedIds, kind]);

  const filteredIncluded = useMemo(() => {
    if (!searchValue.trim()) return includedOptions;
    const q = searchValue.toLowerCase();
    return includedOptions.filter((o) => o.label.toLowerCase().includes(q));
  }, [includedOptions, searchValue]);

  const filteredExcluded = useMemo(() => {
    if (!searchValue.trim()) return excludedOptions;
    const q = searchValue.toLowerCase();
    return excludedOptions.filter((o) => o.label.toLowerCase().includes(q));
  }, [excludedOptions, searchValue]);

  const handleClose = () => {
    setSearchValue("");
    setLocalSelectedIds(selectedValueIds);
    onClose();
  };

  const title =
    kind === "inclusion" ? `Include by: ${ruleTypeLabel}` : `Exclude by: ${ruleTypeLabel}`;
  const countLabel =
    kind === "inclusion"
      ? `${localSelectedIds.length}/${options.length} ${ruleTypeLabel} Included`
      : `${localSelectedIds.length}/${options.length} ${ruleTypeLabel} Excluded`;

  const includedEmptyLabel = kind === "inclusion" ? "None selected" : "None";
  const excludedEmptyLabel = kind === "inclusion" ? "None" : "None excluded";

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      position="right"
      size="380px"
      classNames={{
        header: styles.entityRuleDrawerHeader,
        body: styles.entityRuleDrawerBody,
        content: styles.entityRuleDrawerContent,
      }}
      styles={{ title: { width: "100%" } }}
      title={
        <Group justify="space-between" align="center" w="100%">
          <Text className={styles.entityRuleDrawerTitle}>{title}</Text>
          <UnstyledButton
            onClick={handleClose}
            className={styles.entityRuleDrawerCloseButton}
            type="button"
          >
            <IconX size={24} color="var(--icon-default, #363D4D)" />
          </UnstyledButton>
        </Group>
      }
      withCloseButton={false}
    >
      <Stack gap={0} h="100%" className={styles.entityRuleDrawerStack}>
        {/* Static header: summary + search */}
        <Stack className={styles.entityRuleDrawerStaticHeader}>
          <Text className={styles.entityRuleDrawerSummary}>{countLabel}</Text>
          <TextInput
            value={searchValue}
            onChange={(e) => setSearchValue(e.currentTarget.value)}
            placeholder="Search.."
            w="100%"
            leftSection={<IconSearch size={18} />}
            classNames={{
              input: styles.entityRuleDrawerSearchInput,
              section: styles.entityRuleDrawerSearchSection,
            }}
          />
        </Stack>

        <ScrollArea className={styles.entityRuleDrawerScrollArea} type="auto" scrollbarSize={6}>
          <Stack gap="lg">
            <Stack className={styles.entityRuleDrawerSection}>
              <Text className={styles.entityRuleDrawerSectionTitle}>Included</Text>
              {filteredIncluded.length === 0 ? (
                <Text size="sm" c="dimmed" pl="var(--spacing-x-small)">
                  {includedEmptyLabel}
                </Text>
              ) : (
                filteredIncluded.map((opt) => (
                  <Group key={opt.value} className={styles.entityRuleDrawerItem} align="center">
                    <IconCheck size={16} />
                    <Text className={styles.includedItemLabel}>{opt.label}</Text>
                  </Group>
                ))
              )}
            </Stack>
            <Stack className={styles.entityRuleDrawerSection}>
              <Text className={styles.entityRuleDrawerSectionTitle}>Excluded</Text>
              {filteredExcluded.length === 0 ? (
                <Text size="sm" c="dimmed" pl="var(--spacing-x-small)">
                  {excludedEmptyLabel}
                </Text>
              ) : (
                filteredExcluded.map((opt) => (
                  <Group
                    key={opt.value}
                    className={styles.entityRuleDrawerItemExcluded}
                    align="center"
                  >
                    <IconPointFilled size={16} />
                    <Text className={styles.excludedItemLabel}>{opt.label}</Text>
                  </Group>
                ))
              )}
            </Stack>
          </Stack>
        </ScrollArea>
      </Stack>
    </Drawer>
  );
}
